import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  FiAward,
  FiPlus,
  FiX,
  FiClock,
  FiCheckCircle,
  FiXCircle,
  FiInfo,
  FiFileText,
} from 'react-icons/fi';
import toast from 'react-hot-toast';
import { taxService } from '../../services/apiService';
import LoadingSpinner from '../../components/LoadingSpinner';
import { formatDate, formatDateTime } from '../../utils/formatters';

const PURPOSES = [
  'Bank Loan',
  'Contract Bid',
  'Business License Renewal',
  'Government Tender',
  'Import Permit',
  'Export Permit',
  'Visa Application',
  'Other',
];

const TaxClearance = () => {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);

  // Form state
  const [purpose, setPurpose] = useState('Bank Loan');
  const [periodStart, setPeriodStart] = useState('');
  const [periodEnd, setPeriodEnd] = useState('');
  const [notes, setNotes] = useState('');

  const {
    data: clearanceData,
    isLoading,
    isRefetching,
    refetch,
  } = useQuery('tax-clearance', taxService.listClearances, {
    refetchOnWindowFocus: false,
  });

  const applyMutation = useMutation(taxService.applyClearance, {
    onSuccess: () => {
      toast.success('Application submitted');
      queryClient.invalidateQueries('tax-clearance');
      resetForm();
    },
    onError: (err) => {
      const msg =
        err.response?.data?.message ||
        err.message ||
        'Failed to submit application';
      toast.error(msg);
    },
  });

  const resetForm = () => {
    setPurpose('Bank Loan');
    setPeriodStart('');
    setPeriodEnd('');
    setNotes('');
    setShowForm(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!periodStart || !periodEnd) {
      toast.error('Period start and end dates are required');
      return;
    }
    if (new Date(periodEnd) < new Date(periodStart)) {
      toast.error('End date must be after start date');
      return;
    }

    applyMutation.mutate({
      purpose,
      period_start: periodStart,
      period_end: periodEnd,
      notes: notes.trim() || null,
    });
  };

  if (isLoading) return <LoadingSpinner message="Loading clearances..." />;

  const clearances = clearanceData?.data || [];

  // Stats
  const pendingCount = clearances.filter((c) => c.status === 'pending').length;
  const approvedCount = clearances.filter((c) => c.status === 'approved').length;
  const rejectedCount = clearances.filter((c) => c.status === 'rejected').length;

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Tax Clearance</h1>
          <p className="page-subtitle">
            Apply for and track your Tax Clearance Certificate
          </p>
        </div>
        <div className="page-actions">
          <button
            className="btn btn-secondary"
            onClick={() => refetch()}
            disabled={isRefetching}
          >
            <FiFileText size={18} />
            Refresh
          </button>
          <button
            className="btn btn-primary"
            onClick={() => setShowForm(!showForm)}
          >
            {showForm ? (
              <>
                <FiX size={18} />
                Cancel
              </>
            ) : (
              <>
                <FiPlus size={18} />
                New Application
              </>
            )}
          </button>
        </div>
      </div>

      {/* Info banner */}
      <div
        style={{
          background: '#e8f0fe',
          border: '1px solid #1a73e8',
          borderRadius: '8px',
          padding: '16px',
          marginBottom: '20px',
          display: 'flex',
          gap: '12px',
          alignItems: 'flex-start',
        }}
      >
        <FiInfo size={20} color="#1a73e8" style={{ flexShrink: 0, marginTop: 2 }} />
        <div>
          <div
            style={{
              fontWeight: 600,
              color: '#1e40af',
              marginBottom: '4px',
            }}
          >
            What is a Tax Clearance Certificate?
          </div>
          <div style={{ fontSize: '13px', color: '#1e3a8a', lineHeight: 1.5 }}>
            A Tax Clearance Certificate confirms that your business is
            compliant with all known tax requirements for a specified period.
            It's commonly required for bank loans, contract bids, government
            tenders, and business license renewals.
          </div>
        </div>
      </div>

      {/* Quick stats */}
      {clearances.length > 0 && (
        <div className="stats-grid" style={{ marginBottom: '24px' }}>
          <SmallStat label="Total Applications" value={clearances.length} color="#1a73e8" />
          <SmallStat label="Pending" value={pendingCount} color="#f59e0b" />
          <SmallStat label="Approved" value={approvedCount} color="#16a34a" />
          <SmallStat label="Rejected" value={rejectedCount} color="#dc2626" />
        </div>
      )}

      {/* Application form */}
      {showForm && (
        <div className="card" style={{ marginBottom: '24px' }}>
          <div className="card-header">
            <h2 className="card-title">New Tax Clearance Application</h2>
          </div>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label className="form-label">Purpose *</label>
              <select
                className="form-select"
                value={purpose}
                onChange={(e) => setPurpose(e.target.value)}
              >
                {PURPOSES.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Period Start *</label>
                <input
                  type="date"
                  className="form-input"
                  value={periodStart}
                  onChange={(e) => setPeriodStart(e.target.value)}
                  max={periodEnd || undefined}
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Period End *</label>
                <input
                  type="date"
                  className="form-input"
                  value={periodEnd}
                  onChange={(e) => setPeriodEnd(e.target.value)}
                  min={periodStart || undefined}
                  max={new Date().toISOString().split('T')[0]}
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Notes (optional)</label>
              <textarea
                className="form-textarea"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Any additional information for the LRA reviewer..."
                rows={3}
              />
            </div>

            <div className="form-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={resetForm}
                disabled={applyMutation.isLoading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={applyMutation.isLoading}
              >
                <FiAward size={18} />
                {applyMutation.isLoading
                  ? 'Submitting...'
                  : 'Submit Application'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* List */}
      {clearances.length === 0 ? (
        <div className="card">
          <div className="empty-state">
            <FiAward size={48} />
            <h3>No clearance applications yet</h3>
            <p>
              Apply for a Tax Clearance Certificate for bank loans, tenders,
              or license renewals.
            </p>
            <button
              className="btn btn-primary"
              onClick={() => setShowForm(true)}
            >
              <FiPlus size={18} />
              New Application
            </button>
          </div>
        </div>
      ) : (
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>Application ID</th>
                <th>Purpose</th>
                <th>Period</th>
                <th>Status</th>
                <th>Applied</th>
                <th>Certificate #</th>
                <th>Expires</th>
              </tr>
            </thead>
            <tbody>
              {clearances.map((c) => (
                <tr key={c.clearance_id}>
                  <td>
                    <code style={{ fontSize: '12px' }}>{c.clearance_id}</code>
                  </td>
                  <td>{c.purpose}</td>
                  <td>
                    {formatDate(c.period_start)} — {formatDate(c.period_end)}
                  </td>
                  <td>
                    <ClearanceStatusBadge status={c.status} />
                  </td>
                  <td>{formatDate(c.created_at)}</td>
                  <td>
                    {c.certificate_number ? (
                      <code style={{ fontSize: '12px' }}>
                        {c.certificate_number}
                      </code>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td>
                    {c.expires_at ? formatDate(c.expires_at) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ---------- Helper components ----------

const SmallStat = ({ label, value, color }) => (
  <div
    className="card"
    style={{
      padding: '16px',
      borderLeft: `4px solid ${color}`,
    }}
  >
    <div
      style={{
        fontSize: '26px',
        fontWeight: 700,
        color: color,
        lineHeight: 1.2,
      }}
    >
      {value}
    </div>
    <div style={{ fontSize: '13px', color: '#6b7280', marginTop: 4 }}>
      {label}
    </div>
  </div>
);

const ClearanceStatusBadge = ({ status }) => {
  const colors = {
    pending: { bg: '#fef3c7', color: '#92400e', icon: <FiClock size={12} /> },
    approved: { bg: '#dcfce7', color: '#166534', icon: <FiCheckCircle size={12} /> },
    rejected: { bg: '#fee2e2', color: '#991b1b', icon: <FiXCircle size={12} /> },
    expired: { bg: '#f3f4f6', color: '#6b7280', icon: <FiX size={12} /> },
    revoked: { bg: '#f3f4f6', color: '#6b7280', icon: <FiX size={12} /> },
  };
  const c = colors[status] || colors.pending;
  return (
    <span
      style={{
        background: c.bg,
        color: c.color,
        padding: '4px 10px',
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: 600,
        textTransform: 'capitalize',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
      }}
    >
      {c.icon}
      {status}
    </span>
  );
};

export default TaxClearance;
